import { useState } from "react";
import { ChevronDown, Download } from "lucide-react";
import { downloadCsv, downloadJson, downloadStreamCsv } from "../../utils";
import DropdownMenu from "./DropdownMenu";

interface DownloadMenuProps {
  getRows: () => Promise<Record<string, unknown>[]>;
  filename: string;
  variant?: "button" | "link";
  align?: "left" | "right";
  /** When provided, CSV uses the backend streaming endpoint with this SQL
   *  instead of fetching rows as JSON and converting in the browser.
   *  Supports up to 5,000 rows with no client-side processing. */
  streamCsvSql?: string;
  streamCsvDatasource?: string | null;
  /** Total rows in the full result set — shown in the button label when the
   *  JSON download is capped (e.g. "Download (first 5,000 of 12,400)"). */
  totalRows?: number;
}

export default function DownloadMenu({ getRows, filename, variant = "button", align = "right", streamCsvSql, streamCsvDatasource, totalRows }: DownloadMenuProps) {
  const [loading, setLoading] = useState(false);
  const [lastReturnedCount, setLastReturnedCount] = useState<number | null>(null);

  async function handle(format: "csv" | "json") {
    setLoading(true);
    try {
      if (format === "csv" && streamCsvSql) {
        // Server generates the CSV — no JSON round-trip, no browser-side work
        await downloadStreamCsv(streamCsvSql, filename, streamCsvDatasource);
      } else {
        const rows = await getRows();
        setLastReturnedCount(rows.length);
        if (format === "csv") downloadCsv(rows, filename);
        else downloadJson(rows, filename);
      }
    } catch {
      return;
    } finally {
      setLoading(false);
    }
  }

  const triggerClassName =
    variant === "button"
      ? "flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
      : "flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50";

  const loadingLabel = variant === "link" ? "Downloading..." : "...";

  // Show cap notice on JSON download when results were truncated
  const isCapped = totalRows != null && lastReturnedCount != null && totalRows > lastReturnedCount;
  const capLabel = isCapped ? ` (first ${lastReturnedCount.toLocaleString()} of ${totalRows.toLocaleString()})` : "";
  const baseLabel = `Download${capLabel}`;

  return (
    <DropdownMenu
      align={align}
      disabled={loading}
      triggerLabel={baseLabel}
      triggerClassName={triggerClassName}
      panelClassName="w-28"
      trigger={
        <>
          <Download size={11} aria-hidden="true" />
          {loading ? loadingLabel : baseLabel}
          <ChevronDown size={10} aria-hidden="true" />
        </>
      }
    >
      {(close) => (
        <>
          <button
            onClick={() => { close(); handle("csv"); }}
            className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
          >
            CSV
          </button>
          <button
            onClick={() => { close(); handle("json"); }}
            className="flex w-full items-center gap-2 rounded-b-lg px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
          >
            JSON
          </button>
        </>
      )}
    </DropdownMenu>
  );
}
